import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { useEffect, useState } from 'react';
import formatTravelMode from '../../../../helpers/formatTravelMode';
import ConfirmModal from '../../../ConfirmModal';
import {
  cancelBookingRequest,
  resetCancelBooking,
  getTripConfirmationRequest,
} from '../../../../store/slices/trip';
import { SearchStatus, TravelTypes } from '../../../../constants';
import CancelBookingModalSpinner from '../CancelBookingModalSpinner';
import MarkdownToHtml from '../../../MarkdownToHtml';

const CancelBookingConfirmModal = ({
  onCancel,
  onConfirm,
  mode,
  cancellationPolicy,
  tripSegmentId,
  tripId,
  doVoidFlight,
}) => {
  const dispatch = useDispatch();
  const [isModalOpen, setIsModalOpen] = useState(true);
  const [isSpinnerShown, setIsSpinnerShown] = useState(false);
  const { cancelBookingStatus, cancelBookingError, tripConfirmation } = useSelector(
    (store) => store.trip,
  );
  const { travelAgency } = useSelector((store) => store.user);

  const hasMultipleHotelTripSegment =
    tripConfirmation?.trip?.tripSegments?.filter((ts) => ts.mode === TravelTypes.hotel).length > 1;

  const handleConfirm = () => {
    setIsModalOpen(false);
    dispatch(
      cancelBookingRequest({
        tripId,
        tripSegmentId,
      }),
    );
  };
  const serviceFees = travelAgency &&
    travelAgency.settings &&
    travelAgency.settings.defaultConditionsAndPolicy && (
      <div className="mt-4">
        <MarkdownToHtml
          content={travelAgency.settings.defaultConditionsAndPolicy}
          className="text-black"
        />
      </div>
    );

  useEffect(() => {
    if (cancelBookingStatus === SearchStatus.DONE) {
      if (!isSpinnerShown) return;
      setTimeout(
        () => {
          if (cancelBookingError) {
            setIsSpinnerShown(false);
            onConfirm();
          } else {
            dispatch(getTripConfirmationRequest({ tripId, doRemoveTripConfirmation: true }));
          }
        },
        cancelBookingError ? 6000 : 3000,
      );
    } else {
      setIsSpinnerShown(cancelBookingStatus === SearchStatus.SEARCHING);
    }
    // oxlint-disable-next-line react/exhaustive-deps
  }, [cancelBookingError, cancelBookingStatus, tripId]);

  useEffect(() => {
    dispatch(resetCancelBooking());
    // oxlint-disable-next-line react/exhaustive-deps
  }, []);

  return (
    <>
      <ConfirmModal
        onCancel={onCancel}
        onConfirm={handleConfirm}
        isModalOpen={isModalOpen}
        okButtonLabel={`Yes, ${doVoidFlight ? 'void' : 'cancel'} the ${mode === TravelTypes.hotel ? 'Room' : formatTravelMode(mode)}`}
        cancelButtonLabel="No, not yet"
        event={`Cancel ${formatTravelMode(mode)} Booking`}
        eventDetail={`cancel the ${formatTravelMode(mode)}`}
        message={
          <>
            <div>
              {hasMultipleHotelTripSegment && (
                <div className="bg-red-100 border border-gray font-bold text-sm text-red-600 rounded-sm p-2 mb-2">
                  If you booked multiple rooms, please make sure to cancel each room individually.
                </div>
              )}
              <strong>Note:</strong> Please, review the cancellation policy of your currently booked{' '}
              {formatTravelMode(mode)} rate.
            </div>
            {serviceFees}
            {cancellationPolicy && (
              <>
                <br />
                <b>Cancellation policy of booked {formatTravelMode(mode)}:</b>
                <div>{cancellationPolicy}</div>
              </>
            )}
            <br />
            <div
              className="bg-orange-100 border-l-4 border-orange-500 text-orange-700 p-4"
              role="alert"
            >
              <p>
                This cancellation is final and will be processed immediately. You will receive a
                confirmation email upon completion. It may take a few minutes for the status to be
                updated in your trip. Are you sure you want to continue?
              </p>
            </div>
            {cancelBookingError && (
              <div className="text-red-600 mt-10">
                <b>Error:</b> {cancelBookingError}
              </div>
            )}
          </>
        }
      />
      <CancelBookingModalSpinner mode={formatTravelMode(mode)} isModalOpen={isSpinnerShown} />
    </>
  );
};

CancelBookingConfirmModal.propTypes = {
  onCancel: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  mode: PropTypes.number.isRequired,
  cancellationPolicy: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  tripSegmentId: PropTypes.number.isRequired,
  tripId: PropTypes.string.isRequired,
  doVoidFlight: PropTypes.bool,
};

CancelBookingConfirmModal.defaultProps = {
  cancellationPolicy: undefined,
  doVoidFlight: false,
};

export default CancelBookingConfirmModal;
